import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const SprintCentralSprints = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projId } = useParams();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projId || "");
  const [sprints, setSprints] = useState([]);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingSprint, setEditingSprint] = useState(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProjectId]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const initialForm = {
    name: "",
    goal: "",
    startDate: "",
    endDate: "",
    status: "Planning",
  };

  const [formData, setFormData] = useState(initialForm);

  // -----------------------
  // Sample Data
  // -----------------------

  const demoProjects = [
    { id: 1, name: "Website Redesign 2026" },
    { id: 2, name: "ERP Integration Phase 1" },
    { id: 3, name: "Customer Support Portal" },
  ];

  const demoSprints = [
    {
      id: 1,
      projectId: 1,
      name: "Sprint 1",
      goal: "Setup UI framework",
      startDate: "2026-03-01",
      endDate: "2026-03-15",
      status: "Completed",
    },
    {
      id: 2,
      projectId: 1,
      name: "Sprint 2",
      goal: "Homepage & Navigation",
      startDate: "2026-03-16",
      endDate: "2026-03-30",
      status: "Active",
    },
    {
      id: 3,
      projectId: 2,
      name: "Sprint 1",
      goal: "Inventory integration",
      startDate: "2026-02-01",
      endDate: "2026-02-14",
      status: "Completed",
    },
  ];

  const loadSample = () => {
    setProjects(demoProjects);
    setSprints(demoSprints);
  };

  const handleAddNew = () => {
    if (!selectedProjectId) {
      alert("Please select a project first.");
      return;
    }
    setEditingSprint(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (sprint) => {
    setEditingSprint(sprint);

    setFormData({
      name: sprint.name,
      goal: sprint.goal || "",
      startDate: sprint.startDate || "",
      endDate: sprint.endDate || "",
      status: sprint.status,
    });

    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this sprint?")) return;

    setSprints((prev) => prev.filter((s) => s.id !== id));
  };

  // const handleSubmit = (e) => {

  //   e.preventDefault();

  //   const newSprint = {
  //     id: editingSprint?.id || Date.now(),
  //     projectId: Number(selectedProjectId),
  //     name: formData.name,
  //     goal: formData.goal,
  //     startDate: formData.startDate,
  //     endDate: formData.endDate,
  //     status: formData.status,
  //   };

  //   if (editingSprint) {

  //     setSprints((prev) =>
  //       prev.map((s) => (s.id === editingSprint.id ? newSprint : s))
  //     );

  //   } else {

  //     setSprints((prev) => [...prev, newSprint]);

  //   }

  //   setShowModal(false);
  //   setFormData(initialForm);

  // };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.startDate || !formData.endDate) {
      alert("Please select both Start Date and End Date.");
      return;
    }
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      alert("End Date cannot be earlier than Start Date.");
      return;
    }

    const newSprint = {
      id: editingSprint?.id || Date.now(),
      projectId: Number(selectedProjectId),
      name: formData.name,
      goal: formData.goal,
      startDate: formData.startDate,
      endDate: formData.endDate,
      status: formData.status,
    };

    if (editingSprint) {
      setSprints((prev) =>
        prev.map((s) => (s.id === editingSprint.id ? newSprint : s))
      );
    } else {
      setSprints((prev) => [...prev, newSprint]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // -----------------------
  // Filter by Project
  // -----------------------

  const filteredProjectSprints = sprints.filter(
    (s) => String(s.projectId) === String(selectedProjectId)
  );

  const filtered = filteredProjectSprints.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.goal || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);

  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const statusColor = {
    Planning: "bg-indigo-600",
    Active: "bg-emerald-600",
    Completed: "bg-gray-600",
    "On Hold": "bg-amber-600",
  };

  return (

    <div className="min-h-screen bg-gray-900 text-gray-100 p-2">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            🏃 SprintCentral
          </div>
          <div className="text-xs text-gray-400">
            Manage Sprints & Agile Execution
          </div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 rounded-md"
            >
              🔐
            </button>
          )}
        </div>
      </header>
      <div className="mt-4 min-h-[75vh]">
        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <button
            onClick={loadSample}
            className="text-xs lg:text-sm w-40 lg:w-64 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> Sample Sprints
          </button>

          <button
            onClick={handleAddNew}
            className="text-xs lg:text-sm w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> New Sprint
          </button>

          {!projId && (
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg h-12 px-3 py-2"
            >
              <option value="">Select Project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              type="text"
              placeholder="Search sprints..."
              value={search}
              style={{ width: "100%" }}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg"
            />
          </div>
        </div>
        {showModal && (
          <div className="bg-black w-full rounded-lg p-4 border border-cyan-200 shadow-xl mt-2">          
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg text-cyan-200 font-semibold">
                {editingSprint ? "Edit Sprint" : "New Sprint"}
              </h2>

              <button className="w-12 bg-blue-500" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                placeholder="Sprint Name"
                value={formData.name}
                required
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <textarea
                placeholder="Sprint Goal"
                style={{height: "100px", width:"100%"}}
                value={formData.goal}
                onChange={(e) =>
                  setFormData({ ...formData, goal: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <div className="grid grid-cols-2 gap-3">
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) =>
                    setFormData({ ...formData, startDate: e.target.value })
                  }
                  className="w-48 bg-white border border-gray-700 rounded p-2 text-black appearance-auto"
                />

                <input
                  type="date"
                  value={formData.endDate}
                  min={formData.startDate}
                  onChange={(e) =>
                    setFormData({ ...formData, endDate: e.target.value })
                  }
                  className="ml-2 w-48 bg-white border border-gray-700 rounded p-2 text-black appearance-auto"
                />
              </div>

              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                className="w-40 h-12 bg-gray-900 border border-gray-700 rounded p-2"
              >
                <option value="Planning">Planning</option>
                <option value="Active">Active</option>
                <option value="Completed">Completed</option>
                <option value="On Hold">On Hold</option>
              </select>

              <button
                type="submit"
                className="ml-4 w-40 bg-cyan-600 hover:bg-cyan-500 rounded p-2"
              >
                {editingSprint ? "Update Sprint" : "Create Sprint"}
              </button>
            </form>
        </div>
      )}
        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-4">
          {paginated.map((sprint) => (
            <div
              key={sprint.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5"
            >
              <div className="flex justify-between items-start">
                <h3 className="font-semibold text-white">{sprint.name}</h3>
                <span
                  className={`px-2 py-1 rounded text-xs ${statusColor[sprint.status]}`}
                >
                  {sprint.status}
                </span>
              </div>
              <p className="text-sm text-gray-300 mt-2">
                {sprint.goal || "No sprint goal"}
              </p>
              <div className="text-xs text-gray-400 mt-3">
                Start: {sprint.startDate || "-"} <br />
                End: {sprint.endDate || "-"}
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => handleEdit(sprint)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button
                  onClick={() => handleDelete(sprint.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-10">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>
      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>

  );

};

export default SprintCentralSprints;